<template>
  <div class="project-grid" :class="['project-grid', theme, { 'affiche-mode': afficheMode, 'cv-mode': cvMode }]">
    <!-- Filter buttons -->
    <div class="filter-buttons" 
      :class="{ 'light-theme': isLightTheme, 'dark-theme': !isLightTheme }"
      v-if="!selectedProject">
      <button
        v-for="option in formatOptions"
        :key="option.value"
        @click="filterProjects(option.value)"
        :class="{ active: activeFilter === option.value }"
      >
        {{ option.text }}
      </button>
    </div>

  <div 
    class="secondary-filters" 
    :class="{ 'light-theme': isLightTheme, 'dark-theme': !isLightTheme }"
    v-if="(activeFilter === 'fiction' || activeFilter === 'doc') && !selectedProject"
  >
      <button
        @click="filterProjects(activeFilter, 'short')"
        :class="{ active: secondaryFilter === 'short' }"
      >
        Courts-métrages
      </button>
      <button
        @click="filterProjects(activeFilter, 'feature')"
        :class="{ active: secondaryFilter === 'feature' }"
      >
        Longs-métrages
      </button>
            <button
        @click="filterProjects(activeFilter, 'serie')"
        :class="{ active: secondaryFilter === 'serie' }"
      >
        Séries
      </button>
    </div>

    <div ref="gridTopMarker"></div>
    <!-- Project grid -->


    <div class="grid-container" :class="{ transitioning: isTransitioning }" v-if="!selectedProject && !cvMode">
      <!-- MODE NORMAL (images + clics) -->
      <div
        class="project-card"
        :class="{ 'light-theme': isLightTheme, 'dark-theme': !isLightTheme }"
        v-for="project in filteredProjects"
        :key="project.id + '-' + (afficheMode ? 'affiche' : 'vignette')"
        @click="selectProject(project)"
      >
        <div class="image-card" :class="getRatioClass(project, afficheMode)">
          <div class="image-container" v-if="!project.imageError">
            <img
              :src="getProjectImage(project['name-id'])"
              alt="Image du projet"
              class="project-image"
              @error="project.imageError = true"
              @load="(e) => e.target.classList.remove('loading')"
              @loadstart="(e) => e.target.classList.add('loading')"
            />
          </div>

          <!-- Fallback quand pas d’image -->
          <div class="image-fallback" v-else>
            <template v-if="afficheMode">
              <!-- Vignette en fond -->
              <div class="fallback-wrapper">
                <img
                  :src="`/images/vignettes/${project['name-id']}_VIGN.webp`"
                  alt="Vignette du projet"
                  class="project-image fallback-vignette"
                  @error="project.imageError = true"
                />

                <!-- Texte superposé -->
                <div class="fallback-overlay">
                  <h2 v-html="formatTitle(project.projet)"></h2>
                  <p>{{ project.date }}</p>
                  <p>
                    {{ project.format.includes('serie') ? 'Réalisée par' : 'Réalisé par' }}
                    <span>{{ project.realisat }}</span>
                  </p>
                </div>
              </div>
            </template>

            <template v-else>
              <!-- Fallback texte normal pour le mode vignette -->
              <h2 v-html="formatTitle(project.projet)"></h2>
              <p>{{ project.date }}</p>
              <p>
                {{ project.format.includes('serie') ? 'Réalisée par' : 'Réalisé par' }}
                <span>{{ project.realisat }}</span>
              </p>
            </template>
          </div>
        </div>  

        <div class="card-detail" v-if="!afficheMode">
          <div class="flex-row space-between">
            <h2 v-html="formatTitle(project.projet)"></h2>
            <div class="flex-row">
              <hr class="solid">
              <div class="flex-col formats">
                <span class="self-end">{{ project.format.includes('music') ? 'musique' : '' }}</span>
                <span class="self-end">{{ project.format.includes('art') ? 'art' : '' }}</span>
                <span class="self-end">{{ project.format.includes('short') ? 'court-métrage' : '' }}</span>
                <span class="self-end">{{ project.format.includes('feature') ? 'long-métrage' : '' }}</span>
                <span class="self-end">{{ project.format.includes('serie') ? 'série' : '' }}</span>
                <span class="self-end">{{ project.format.includes('fiction') ? 'fiction' : '' }}</span>
                <span class="self-end">{{ project.format.includes('doc') ? 'documentaire' : '' }}</span>
              </div>
            </div>
          </div>
          <div><hr class="solid"></div>
          <div class="flex-row space-between">
            <div class="date"><span>{{ project.date }}</span></div>
            <div>
              <h3>{{ project.format.includes('serie') ? 'Réalisée par' : 'Réalisé par' }} <span>{{ project.realisat }}</span></h3>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- MODE CV (texte uniquement, en liste) -->
    <div v-if="!selectedProject && cvMode" class="cv-list">
      <div
        class="cv-item"
        v-for="project in filteredProjects"
        :key="project.id"
      >
        <!-- Première ligne : projet / date / formats -->
        <div class="cv-line cv-line-top flex-row">
          <h2 v-html="formatTitle(project.projet)"></h2>
          <span class="date">{{ project.date }}</span>
          <span class="separator"></span>
          <div class="flex-row formats">
            <span v-if="project.format.includes('music')">musique</span>
            <span v-if="project.format.includes('art')">art</span>
            <span v-if="project.format.includes('short')">court-métrage</span>
            <span v-if="project.format.includes('feature')">long-métrage</span>
            <span v-if="project.format.includes('serie')">série</span>
            <span v-if="project.format.includes('fiction')">fiction</span>
            <span v-if="project.format.includes('doc')">documentaire</span>
          </div>
        </div>

        <!-- Seconde ligne : réalisateur / production -->
        <div class="cv-line cv-line-bottom flex-row">
          <div class="realisateur">
            <span>{{ project.format.includes('serie') ? 'Réalisée par ' : 'Réalisé par ' }}</span>
            <strong>{{ project.realisat }}</strong>
          </div>
          <div class="production" v-if="project.prod || project.prod2">
            <span class="separator"></span>
            <span>Produit par  </span>
            <strong v-if="project.prod">{{ project.prod }}</strong>
            <strong v-if="project.prod2">& {{ project.prod2 }}</strong>
          </div>
        </div>        

      </div>
    </div>


    <!-- Project details -->
    <ProjectDetail
      v-if="selectedProject"
      :project="selectedProject"
      :numberOfImages="numberOfImages.length"
      :closeProjectDetail="closeProjectDetail"
      :getCarouselImage="getCarouselImage"
      :isYouTubeVideo="isYouTubeVideo"
      :isVimeoVideo="isVimeoVideo"
      :getYouTubeEmbedUrl="getYouTubeEmbedUrl"
      :getVimeoEmbedUrl="getVimeoEmbedUrl"
      :theme="theme"
    />

<!-- Bouton flottant de changement de mode -->
<div v-if="!selectedProject" class="floating-buttons">

  <button class="toggle-mode-btn" @click="cvMode = !cvMode">
    {{ cvMode ? 'Visuel' : 'CV' }}
  </button>

  <button v-if="!cvMode" class="toggle-mode-btn" @click="afficheMode = !afficheMode">
    {{ afficheMode ? 'Stills' : 'Poster' }}
  </button>
</div>

  </div>
  
</template>

<script>
import { ref, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import ProjectDetail from './ProjectDetail.vue'; 
import "./ProjectGrid.css";
import { watchEffect } from 'vue';
import { watch } from 'vue';


export default {
  components: {
    ProjectDetail,
  },
  props: {
    filteredProjects: {
      type: Array,
      required: true,
    },
    formatOptions: {
      type: Array,
      required: true,
    },
    activeFilter: {
      type: String,
      default: null, // Vous pouvez définir une valeur par défaut si nécessaire
    },
    theme: {
      type: String,
      required: true, // Assurez-vous que le thème est requis
    },
    selectedProject: {
      type: Object,
      default: null,
    },
  },
  setup() {
    const projects = ref([]);
    const filteredProjects = ref([]);
    const secondaryFilter = ref(null);
    const selectedProject = ref(null);
    const route = useRoute();
    const loading = ref(true);
    const error = ref(null);
    const activeFilter = ref(null);
    const numberOfImages = ref([]);
    const lightboxImageIndex = ref(null);
    const scrollPosition = ref(0);
    const afficheMode = ref(false);
    const cvMode = ref(false);
    const isTransitioning = ref(false);
    const slugify = (text) => {
      return text
        .toString()
        .normalize("NFD") // remove accents
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-') // remplace tout par des tirets
        .replace(/^-+|-+$/g, '');    // supprime les tirets en trop
    };

    const formatTitle = (title) => {
      return cvMode.value
        ? title.replace(/\|/g, ' ') // mode CV → pas de saut
        : title.replace(/\|/g, '<br>'); // mode visuel → saut de ligne
    };

    const formatOptions = ref([
      { value: null, text: "Tous" },
      { value: "fiction", text: "Fiction" },
      { value: "doc", text: "Documentaire" },
      { value: "music", text: "Musique" },
      { value: "art", text: "Art" },
    ]);

    const fetchProjects = async () => {
      try {
        const response = await fetch("https://julian-nouveau.com/api.php");
        if (!response.ok) throw new Error("Erreur lors de la récupération des projets");
        const data = await response.json();

        // Sort projects by sortOrder in descending order
        projects.value = data.sort((a, b) => b.sortOrder - a.sortOrder);
        filteredProjects.value = projects.value;
      } catch (err) {
        error.value = err.message;
      } finally {
        loading.value = false;
      }
    };

    const filterProjects = (primaryFormat, secondary = null) => {
      // Si on change de catégorie principale (fiction/doc/music), on réinitialise
      if (activeFilter.value !== primaryFormat) {
        activeFilter.value = primaryFormat;
        secondaryFilter.value = null;
      } else if (secondary) {
        // Si on clique sur le même filtre secondaire => toggle (désactive)
        secondaryFilter.value =
          secondaryFilter.value === secondary ? null : secondary;
      }

      filteredProjects.value = projects.value
        .filter((project) => {
          const formats = project.format.split(",");
          const hasPrimary = primaryFormat ? formats.includes(primaryFormat) : true;
          const hasSecondary = secondaryFilter.value
            ? formats.includes(secondaryFilter.value)
            : true;
          return hasPrimary && hasSecondary;
        })
        .sort((a, b) => b.sortOrder - a.sortOrder);
    };


    const getFormatTexts = (formats) => {
      const excludedFormats = ["short", "feature"];
      return formats.split(",")
        .map((format) => format.trim())
        .filter((format) => !excludedFormats.includes(format))
        .map((format) => formatOptions.value.find((option) => option.value === format)?.text || format)
        .join(", ");
    };

    const getProjectImage = (nameId) => {
      return afficheMode.value
        ? `/images/affiches/${nameId}_AFFICHE.webp`   // chemin des affiches verticales
        : `/images/vignettes/${nameId}_VIGN.webp`;    // chemin des vignettes 16:9
    };

    const selectProject = (project) => {
      const currentState = history.state || {};
      history.replaceState({ ...currentState, scrollPosition: window.scrollY }, '');

      selectedProject.value = project;
      numberOfImages.value = Array.from({ length: project.stills }, (_, i) => i + 1);

      const slug = slugify(project.projet);
      history.pushState({ projectSlug: slug }, '', `#${slug}`);

      // Scroll vers le haut de la page
      window.scrollTo({
        top: 0,
        behavior: 'smooth', // Ajoute un défilement fluide
      });
 
    };

    const getCarouselImage = (nameId, index) => {
      return `/images/CAROUSEL/${nameId}/${nameId}_${index}.webp`;
    };

 

    const openLightbox = (index) => {
      lightboxImageIndex.value = index;
    };  

    const closeLightbox = () => {
      lightboxImageIndex.value = null;
    };

    const closeProjectDetail = () => {
      history.back();
    };

    const isYouTubeVideo = (url) => {
      return url.includes("youtube.com") || url.includes("youtu.be");
    };

    const isVimeoVideo = (url) => {
      return url.includes("vimeo.com");
    };

    const getYouTubeEmbedUrl = (url) => {
      const videoId = url.split("v=")[1]?.split("&")[0] || url.split("/").pop();
      return `https://www.youtube.com/embed/${videoId}`;
    };

    const getVimeoEmbedUrl = (url) => {
      const videoId = url.split("/").pop();
      return `https://player.vimeo.com/video/${videoId}`;
    };

    const getRatioClass = (project, afficheModeValue = false) => {
      // Si on est en mode affiche → toutes les cartes passent en 2:3
      if (afficheModeValue) return 'ratio-2-3';
      // Sinon, chaque projet peut avoir son ratio (ou 16-9 par défaut)
      const ratio = project.ratio || '16-9';
      return `ratio-${ratio}`;
    };

    
    onMounted(() => {

      // Lire les préférences sauvegardées
      const savedMode = localStorage.getItem('afficheMode');
      afficheMode.value = savedMode === 'true';  

      const savedCV = localStorage.getItem('cvMode');
      cvMode.value = savedCV === 'true';  

      fetchProjects();

      window.addEventListener('popstate', (event) => {
        if (event.state?.projectSlug) {
          // ouvrir projet
          const project = projects.value.find(p => slugify(p.projet) === event.state.projectSlug);
          if (project) {
            selectedProject.value = project;
            numberOfImages.value = Array.from({ length: project.stills }, (_, i) => i + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else {
          // fermer projet, revenir à la position sauvegardée
          selectedProject.value = null;
          const pos = event.state?.scrollPosition || 0;
          nextTick(() => {
            window.scrollTo({ top: pos, behavior: 'smooth' });
          });
        }
      });

      watchEffect(() => {
        if (projects.value.length > 0 && window.location.hash) {
          const slugInUrl = window.location.hash.substring(1); // retire le #
          
          const project = projects.value.find(p => slugify(p.projet) === slugInUrl);
          if (project) {
            if (!selectedProject.value || selectedProject.value.id !== project.id) {
              selectedProject.value = project;
              numberOfImages.value = Array.from({ length: project.stills }, (_, i) => i + 1);
            }
            window.scrollTo({ top: 0, behavior: 'auto' });
          }
        }
      });

      watch(afficheMode, async (newValue) => {
        localStorage.setItem('afficheMode', newValue);

        // Force une légère transition fluide
        isTransitioning.value = true;

        // Réinitialiser les erreurs d’image
        filteredProjects.value.forEach((project) => {
          project.imageError = false;
        });

        // Forcer Vue à recalculer les images après une micro-pause
        await nextTick();
        filteredProjects.value = [...filteredProjects.value];

        // Petit délai pour le fondu visuel
        setTimeout(() => {
          isTransitioning.value = false;
        }, 150);
      });


      watch(cvMode, (newValue) => {
        localStorage.setItem('cvMode', newValue);
      });

    });


    return {
      projects,
      filteredProjects,
      selectedProject,
      loading,
      error,
      formatOptions,
      filterProjects,
      selectProject,
      closeProjectDetail,
      getProjectImage,
      getCarouselImage,
      numberOfImages,
      activeFilter,
      secondaryFilter,
      lightboxImageIndex,
      openLightbox,
      closeLightbox,
      isYouTubeVideo,
      isVimeoVideo,
      getYouTubeEmbedUrl,
      getVimeoEmbedUrl,
      getFormatTexts,
      formatTitle,
      getRatioClass,
      afficheMode,
      cvMode,
      isTransitioning,
    };
  },
  computed: {
    isLightTheme() {
      return this.theme === 'light';
    },
  },
};
</script>

<style>
</style>
